import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useT } from "../../i18n/index.js";

/**
 * Provider de modais globais para substituir `window.prompt()` e
 * `window.confirm()` nativos.
 *
 * Uso (em qualquer componente):
 *
 *   const modals = useModals();
 *
 *   // No lugar de prompt("Motivo:")
 *   const reason = await modals.promptReason({
 *     title: "Motivo para desativar",
 *     placeholder: "Ex: cliente encerrou contrato",
 *     required: true,
 *   });
 *   if (!reason) return;
 *
 *   // No lugar de confirm("Tem certeza?")
 *   const ok = await modals.confirm({
 *     title: "Aplicar TODAS as regras?",
 *     message: "Vai processar todas as transações elegíveis. Continuar?",
 *     tone: "warning",
 *   });
 *   if (!ok) return;
 *
 *   // Prompt numérico (substitui prompt(...) + Number(...))
 *   const amount = await modals.promptNumber({
 *     title: "Montant payé",
 *     defaultValue: 100,
 *     min: 0,
 *   });
 *   if (amount === null) return;
 *
 * O provider mantém uma "fila" de modais. Múltiplas chamadas concorrentes
 * empilham (somente o topo é visível), mas em prática só 1 modal por vez é
 * disparado por interação humana.
 */

export type ConfirmTone = "default" | "danger" | "warning";

export interface PromptReasonOpts {
  title: string;
  message?: string;
  placeholder?: string;
  /** Quando true, botão Confirmar fica disabled enquanto vazio. Default true. */
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Valor pré-preenchido. */
  defaultValue?: string;
  /** Tamanho máximo do input. Default 500. */
  maxLength?: number;
  /** Render como textarea em vez de input. */
  multiline?: boolean;
}

export interface ConfirmOpts {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

export interface AlertOpts {
  /** Título do modal. Default "Aviso". */
  title?: string;
  message?: string;
  /** "danger" p/ erros (vermelho), "warning", ou "default" (lime). */
  tone?: ConfirmTone;
  okLabel?: string;
}

export interface PromptNumberOpts {
  title: string;
  message?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  /** Quando true, exige valor (não pode confirmar vazio). Default true. */
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Sufixo (ex: "€", "%"). Apenas visual. */
  suffix?: string;
}

interface ModalsApi {
  promptReason: (opts: PromptReasonOpts) => Promise<string | null>;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  promptNumber: (opts: PromptNumberOpts) => Promise<number | null>;
  /** Substitui window.alert(): modal informativo com 1 botão (OK). */
  alert: (opts: AlertOpts) => Promise<void>;
}

type StackItem =
  | { kind: "reason"; id: number; opts: PromptReasonOpts; resolve: (v: string | null) => void }
  | { kind: "confirm"; id: number; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "number"; id: number; opts: PromptNumberOpts; resolve: (v: number | null) => void }
  | { kind: "alert"; id: number; opts: AlertOpts; resolve: () => void };

const ModalsContext = createContext<ModalsApi | null>(null);

let nextId = 1;

export function ModalsProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<StackItem[]>([]);

  const closeTop = useCallback((cb: (item: StackItem) => void) => {
    setStack((prev) => {
      const top = prev[prev.length - 1];
      if (top) cb(top);
      return prev.slice(0, -1);
    });
  }, []);

  const promptReason = useCallback(
    (opts: PromptReasonOpts) =>
      new Promise<string | null>((resolve) => {
        setStack((prev) => [...prev, { kind: "reason", id: nextId++, opts, resolve }]);
      }),
    [],
  );

  const confirm = useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        setStack((prev) => [...prev, { kind: "confirm", id: nextId++, opts, resolve }]);
      }),
    [],
  );

  const promptNumber = useCallback(
    (opts: PromptNumberOpts) =>
      new Promise<number | null>((resolve) => {
        setStack((prev) => [...prev, { kind: "number", id: nextId++, opts, resolve }]);
      }),
    [],
  );

  const alert = useCallback(
    (opts: AlertOpts) =>
      new Promise<void>((resolve) => {
        setStack((prev) => [...prev, { kind: "alert", id: nextId++, opts, resolve }]);
      }),
    [],
  );

  const top = stack[stack.length - 1];

  const api: ModalsApi = { promptReason, confirm, promptNumber, alert };

  return (
    <ModalsContext.Provider value={api}>
      {children}
      {top?.kind === "reason" && (
        <ReasonModal
          key={top.id}
          opts={top.opts}
          onSubmit={(v) =>
            closeTop((item) => {
              if (item.kind === "reason") item.resolve(v);
            })
          }
          onCancel={() =>
            closeTop((item) => {
              if (item.kind === "reason") item.resolve(null);
            })
          }
        />
      )}
      {top?.kind === "confirm" && (
        <ConfirmModalView
          key={top.id}
          opts={top.opts}
          onConfirm={() =>
            closeTop((item) => {
              if (item.kind === "confirm") item.resolve(true);
            })
          }
          onCancel={() =>
            closeTop((item) => {
              if (item.kind === "confirm") item.resolve(false);
            })
          }
        />
      )}
      {top?.kind === "number" && (
        <NumberModal
          key={top.id}
          opts={top.opts}
          onSubmit={(v) =>
            closeTop((item) => {
              if (item.kind === "number") item.resolve(v);
            })
          }
          onCancel={() =>
            closeTop((item) => {
              if (item.kind === "number") item.resolve(null);
            })
          }
        />
      )}
      {top?.kind === "alert" && (
        <AlertModalView
          key={top.id}
          opts={top.opts}
          onClose={() =>
            closeTop((item) => {
              if (item.kind === "alert") item.resolve();
            })
          }
        />
      )}
    </ModalsContext.Provider>
  );
}

export function useModals(): ModalsApi {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    throw new Error(
      "useModals(): ModalsProvider missing. Wrap a árvore React em <ModalsProvider> (ver main.tsx).",
    );
  }
  return ctx;
}

// ───────────────────────────────────────────────────────────────────────
// Componentes internos
// ───────────────────────────────────────────────────────────────────────

const TONE_CONFIRM_BTN: Record<ConfirmTone, string> = {
  default: "bg-lime hover:bg-lime-bright text-on-lime",
  danger: "bg-red-600 hover:bg-red-500",
  warning: "bg-amber-600 hover:bg-amber-500",
};

const TONE_HEADER: Record<ConfirmTone, string> = {
  default: "text-neutral-100",
  danger: "text-red-300",
  warning: "text-amber-300",
};

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm focus:border-lime outline-none disabled:opacity-50";

function ModalShell({
  title,
  tone = "default",
  onClose,
  children,
}: {
  title: string;
  tone?: ConfirmTone;
  onClose: () => void;
  children: ReactNode;
}) {
  // ESC fecha (= cancela)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const t = useT();

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[100]"
      onClick={(e) => {
        // Click no backdrop fecha
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-neutral-800 flex items-center justify-between">
          <h2 className={`text-base font-semibold ${TONE_HEADER[tone]}`}>{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
            aria-label={t("modals.close")}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ReasonModal({
  opts,
  onSubmit,
  onCancel,
}: {
  opts: PromptReasonOpts;
  onSubmit: (v: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(opts.defaultValue ?? "");
  const t = useT();
  const tone = opts.tone ?? "default";
  const required = opts.required ?? true;
  const trimmed = value.trim();
  const canSubmit = required ? trimmed.length > 0 : true;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(trimmed);
  }

  return (
    <ModalShell title={opts.title} tone={tone} onClose={onCancel}>
      <form onSubmit={handleSubmit} className="p-5 space-y-3">
        {opts.message && <p className="text-sm text-neutral-400">{opts.message}</p>}
        {opts.multiline ? (
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={opts.placeholder}
            maxLength={opts.maxLength ?? 500}
            rows={4}
            className={`${inputClass} resize-y`}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={opts.placeholder}
            maxLength={opts.maxLength ?? 500}
            className={inputClass}
          />
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {opts.cancelLabel ?? t("modals.cancel")}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`rounded-md ${TONE_CONFIRM_BTN[tone]} disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-1.5 text-sm font-medium`}
          >
            {opts.confirmLabel ?? t("modals.confirm")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ConfirmModalView({
  opts,
  onConfirm,
  onCancel,
}: {
  opts: ConfirmOpts;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const tone = opts.tone ?? "default";
  return (
    <ModalShell title={opts.title} tone={tone} onClose={onCancel}>
      <div className="p-5 space-y-4">
        {opts.message && <p className="text-sm text-neutral-300">{opts.message}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {opts.cancelLabel ?? t("modals.cancel")}
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            className={`rounded-md ${TONE_CONFIRM_BTN[tone]} text-white px-4 py-1.5 text-sm font-medium`}
          >
            {opts.confirmLabel ?? t("modals.confirm")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function AlertModalView({ opts, onClose }: { opts: AlertOpts; onClose: () => void }) {
  const t = useT();
  const tone = opts.tone ?? "default";
  return (
    <ModalShell title={opts.title ?? t("modals.alertTitle")} tone={tone} onClose={onClose}>
      <div className="p-5 space-y-4">
        {opts.message && (
          <p className="text-sm text-neutral-300 whitespace-pre-wrap">{opts.message}</p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            autoFocus
            onClick={onClose}
            className={`rounded-md ${TONE_CONFIRM_BTN[tone]} text-white px-4 py-1.5 text-sm font-medium`}
          >
            {opts.okLabel ?? t("modals.ok")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function NumberModal({
  opts,
  onSubmit,
  onCancel,
}: {
  opts: PromptNumberOpts;
  onSubmit: (v: number) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState<string>(
    opts.defaultValue !== undefined ? String(opts.defaultValue) : "",
  );
  const t = useT();
  const tone = opts.tone ?? "default";
  const required = opts.required ?? true;
  const trimmed = value.trim();
  const parsed = trimmed === "" ? null : Number(trimmed.replace(",", "."));
  const valid =
    parsed !== null &&
    Number.isFinite(parsed) &&
    (opts.min === undefined || parsed >= opts.min) &&
    (opts.max === undefined || parsed <= opts.max);
  const canSubmit = required ? valid : trimmed === "" || valid;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || parsed === null) return;
    onSubmit(parsed);
  }

  return (
    <ModalShell title={opts.title} tone={tone} onClose={onCancel}>
      <form onSubmit={handleSubmit} className="p-5 space-y-3">
        {opts.message && <p className="text-sm text-neutral-400">{opts.message}</p>}
        <div className="flex items-center gap-2">
          <input
            autoFocus
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            {...(opts.min !== undefined ? { min: opts.min } : {})}
            {...(opts.max !== undefined ? { max: opts.max } : {})}
            {...(opts.step !== undefined ? { step: opts.step } : {})}
            className={`${inputClass} font-mono text-right flex-1`}
          />
          {opts.suffix && <span className="text-sm text-neutral-400 font-mono">{opts.suffix}</span>}
        </div>
        {!valid && trimmed !== "" && (
          <p className="text-xs text-red-400">
            {t("modals.invalidValue")}
            {opts.min !== undefined ? ` (${t("modals.minAbbr")} ${opts.min})` : ""}
            {opts.max !== undefined ? ` (${t("modals.maxAbbr")} ${opts.max})` : ""}.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {opts.cancelLabel ?? t("modals.cancel")}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`rounded-md ${TONE_CONFIRM_BTN[tone]} disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-1.5 text-sm font-medium`}
          >
            {opts.confirmLabel ?? t("modals.confirm")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
